import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Combobox } from '../../components/ui/Combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { listAccounts } from '../accounts';
import { BOOKING_TIMEZONES, DEFAULT_BOOKING_TIMEZONE } from './timezones';
import { createBookingCalendar, getAfDefaults, getCalendarStatus, listBookingCalendars } from './api';
import { AfCalendarFields } from './AfCalendarFields';
import { afClubError, afConfigFromDraft, emptyAfDraft, type AfFormDraft } from '../../lib/appointments/afCalendarForm';

// Booking calendars — the list, and creating one.
//
// One calendar per bookable service per client: a 30-minute test drive and a
// 90-minute handover are two calendars, because the RULES live on the calendar
// rather than on the salesperson.

export function BookingCalendarsPage() {
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState('');
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(DEFAULT_BOOKING_TIMEZONE);
  const [creating, setCreating] = useState(false);
  const [provider, setProvider] = useState<'standard' | 'anytime_fitness'>('standard');
  const [af, setAf] = useState<AfFormDraft>(emptyAfDraft());

  const { data: calendars = [], isLoading } = useQuery({
    queryKey: ['booking-calendars'],
    queryFn: () => listBookingCalendars(),
  });
  const { data: accounts = [] } = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const { data: status } = useQuery({ queryKey: ['appointments-status'], queryFn: getCalendarStatus });

  const accountOptions = useMemo(
    () =>
      accounts
        .filter((a) => a.status !== 'cancelled')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts],
  );

  const create = useMutation({
    mutationFn: () =>
      createBookingCalendar({
        accountId,
        name: name.trim(),
        timezone,
        ...(provider === 'anytime_fitness' ? { anytimeFitness: afConfigFromDraft(af) } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking-calendars'] });
      setName('');
      setAccountId('');
      setProvider('standard');
      setAf(emptyAfDraft());
      setCreating(false);
      toast.success('Calendar created. Connect the staff who can take bookings from it.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const canCreate =
    accountId !== '' && name.trim() !== '' && timezone !== '' && (provider === 'standard' || afClubError(af.locationNumber) === null);

  // PREFILL the club id from the account's Anytime Fitness lead route (never overwrites what was typed).
  const prefillClub = (id: string) => {
    if (id === '') return;
    void getAfDefaults(id)
      .then((d) => {
        if (d.clubId) setAf((cur) => (cur.locationNumber.trim() === '' ? { ...cur, locationNumber: d.clubId! } : cur));
      })
      .catch(() => undefined);
  };
  const pickAccount = (id: string) => {
    setAccountId(id);
    if (provider === 'anytime_fitness') prefillClub(id);
  };

  return (
    <div>
      <PageHeader
        title="Booking calendars"
        description="Bookable calendars for clients — the rules live on the calendar, and salespeople are connected to it."
        actions={
          <Button onClick={() => setCreating((v) => !v)}>
            <Plus className="mr-1 h-4 w-4" />
            New calendar
          </Button>
        }
      />

      {status && !status.calendarConfigured && (
        // ⚠️ Said up front. Without the service account nothing can be read, so a
        // page full of calendars that silently offer nothing would be worse than
        // a plain statement of why.
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          The Kyck calendar service account has not been set up on this environment, so no
          calendar can be read yet. Calendars can still be configured.
        </div>
      )}

      {creating && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>New booking calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="new-calendar-account">Client</Label>
                <Combobox
                  value={accountId}
                  onValueChange={pickAccount}
                  options={accountOptions}
                  placeholder="Pick a client"
                  searchPlaceholder="Search clients"
                  emptyText="No matching clients"
                  id="new-calendar-account"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-calendar-name">Name</Label>
                <Input
                  id="new-calendar-name"
                  className="mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Test drive"
                />
              </div>
              <div>
                <Label htmlFor="new-calendar-timezone">Timezone</Label>
                <SelectField
                  value={timezone}
                  onValueChange={setTimezone}
                  options={BOOKING_TIMEZONES}
                  id="new-calendar-timezone"
                  className="mt-1"
                  aria-label="Calendar timezone"
                />
              </div>
            </div>
            <div className="mt-4 max-w-sm">
              <Label htmlFor="new-calendar-provider">Calendar type</Label>
              <SelectField
                value={provider}
                onValueChange={(v) => {
                  setProvider(v === 'anytime_fitness' ? 'anytime_fitness' : 'standard');
                  if (v === 'anytime_fitness') prefillClub(accountId);
                }}
                options={[
                  { value: 'standard', label: 'Kyck calendar (own hours and staff)' },
                  { value: 'anytime_fitness', label: 'Anytime Fitness club calendar' },
                ]}
                id="new-calendar-provider"
                className="mt-1"
                aria-label="Calendar type"
              />
            </div>
            {provider === 'anytime_fitness' && (
              <div className="mt-4">
                <AfCalendarFields value={af} onChange={setAf} accountId={accountId || null} />
              </div>
            )}
            <div className="mt-4 flex items-center gap-2">
              <Button disabled={!canCreate || create.isPending} onClick={() => create.mutate()}>
                {create.isPending && <Spinner className="mr-1 h-4 w-4" />}
                Create
              </Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Starts with weekday 9–5 opening hours and the standard booking rules. Both are
              editable, and nothing is offered until a salesperson is connected.
            </p>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Spinner />
      ) : calendars.length === 0 ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-muted-foreground">
          No booking calendars yet.
        </p>
      ) : (
        <div className="space-y-2">
          {calendars.map((calendar) => (
            <Link
              key={calendar.id}
              to={`/booking-calendars/${calendar.id}`}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3 hover:bg-accent/40"
            >
              <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="font-medium">{calendar.name}</span>
              <span className="text-sm text-muted-foreground">{calendar.accountName ?? '—'}</span>
              <span className="text-xs text-muted-foreground">{calendar.timezone}</span>
              <span className="text-xs text-muted-foreground">
                {calendar.rules.meetingDurationMinutes} min · every{' '}
                {calendar.rules.meetingIntervalMinutes} min
              </span>
              {calendar.anytimeFitness && (
                <Badge variant="neutral">
                  Anytime Fitness {calendar.anytimeFitness.locationNumber}
                  {calendar.anytimeFitness.testMode ? ' (test mode)' : ''}
                </Badge>
              )}
              {!calendar.enabled && (
                <Badge variant="neutral" className="ml-auto">
                  Turned off
                </Badge>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
